<template>
  <div class="tab-type-presentation">
    <!-- TAB: CONTENT -->
    <ul v-if="rightNavActiveTab === rightNavTabNames.CONTENT">
      <li
        @click="activeTabType()"
        class="tab-type-presentation-item"
        :class="{ active: isTabTypeActive }"
        v-if="isTabTypeVisible"
      >
        {{ $i18nPresenter.t('Type') }}
      </li>
      <li
        @click="activeTabSlide(2)"
        class="tab-type-presentation-item"
        :class="{ 'active': $store.state.slideActive.activeTab == 2,
                  'disabled': !isTabContentVisible
        }"
      >
        <span v-if="isContentSlide">{{ $i18nPresenter.t('Templates') }}</span>
        <span v-else>{{ $i18nPresenter.t('Content') }}</span>
      </li>
    </ul>
    <!-- TAB: AUDIO -->
    <ul v-if="rightNavActiveTab === rightNavTabNames.AUDIO">
      <li
        @click="activeAudioTab(rightPanelContainerActiveTabs.TAB_AUDIO_SLIDE)"
        class="tab-type-presentation-item"
        :class="{ 'active': isTabAudioSlideActive,
                  'disabled': isAudioTabDisabled
        }"
      >
        {{ $i18nPresenter.t('Slide') }}
      </li>
      <li
        @click="activeAudioTab(rightPanelContainerActiveTabs.TAB_AUDIO_PRESENTATION)"
        class="tab-type-presentation-item"
        :class="{ 'active': isTabAudioPresentationActive,
                  'disabled': isAudioTabDisabled
        }"
      >
        {{ $i18nPresenter.t('Presentation') }}
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import utils from '@/util'
import Config from '@/config'
import EventBus from '@/eventBus'
import eventCategories from '@/constant/eventCategories'

export default {
  props: {
    showingAudioTab: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isTabAIAddingSlide: false,
    }
  },
  computed: {
    ...mapGetters('rightNav', ['rightNavTabNames', 'rightNavActiveTab', 'isStaticTab', 'isAudioTabDisabled']),
    ...mapGetters('rightPanelContainer', [
      'isTabAudioSlideActive',
      'isTabAudioPresentationActive',
      'rightPanelContainerActiveTabs',
    ]),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      showingSlideGrid: (state) => state.indicators.showingSlideGrid,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      selectedSlideIds: (state) => state.selections.selectedSlideIds,
    }),

    isTabTypeVisible() {
      return this.slideActive.type != 'leaderboard' && this.selectedSlideIds.length <= 1
    },
    isTabContentVisible() {
      return this.slideActive.type && this.selectedSlideIds.length <= 1 && !this.isTabAIAddingSlide
    },
    isTabDesignVisible() {
      return this.slideActive.type && !this.isTabAIAddingSlide
    },
    isTabAudioVisible() {
      return this.showingAudioTab && !this.isTabAIAddingSlide
    },
    isTabTypeActive() {
      return this.slideActive.activeTab == 1
    },
    contrastReminderVisible() {
      try {
        // For more info: https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o
        const { slideActive } = this.$store.state
        const textColour = utils.hexToRgb(slideActive.textColour)
        const baseColour = utils.hexToRgb(slideActive.baseColour)
        if (!textColour || !baseColour) return false
        const textColourLuminance = utils.getColourLuminance(
          textColour.r,
          textColour.g,
          textColour.b,
        )
        const baseColourLuminance = utils.getColourLuminance(
          baseColour.r,
          baseColour.g,
          baseColour.b,
        )
        const contrastRatio = utils.getContrastRatio(
          textColourLuminance,
          baseColourLuminance,
        )

        // Question title font size's > 18px ( > 18pt) => AA-level passed when contrast < 1/3
        const AALevelPassedForLargeText = 1 / 3
        slideActive.contrastReminderVisible =
          contrastRatio >= AALevelPassedForLargeText
        return contrastRatio >= AALevelPassedForLargeText
      } catch (error) {
        Sentry.captureException(error)
        return false
      }
    },
    isEu() {
      return Config.region === 'eu'
    },
    isContentSlide() {
      return this.slideActive.type === 'freestyle'
    },
  },
  mounted() {
    EventBus.$on('ActiveTab: hide-content-design-audio-tabs', () => {
      this.isTabAIAddingSlide = true
    })
  },
  destroyed() {
    EventBus.$off('ActiveTab: hide-content-design-audio-tabs')
  },
  methods: {
    ...mapActions('rightPanelContainer', ['setRightPanelContainerActiveTabAction']),

    _activeTabSlide(index) {
      this.$store.dispatch('updateSlideActive', {
        activeTab: index,
      })
      const isTabType = index == 1
      const isTabAI = index == 5
      if (isTabType || isTabAI) return
      utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Right column > Switch tab', null, null, {
        slideId: this.slideActive.id,
        tabIndex: index,
      })
    },

    activeTabSlide(tabIndex) {
      if (tabIndex === 2 && !this.isTabContentVisible) return false
      if (tabIndex === 3 && !this.isTabDesignVisible) return false
      if (tabIndex === 4 && !this.isTabAudioVisible) return false
      return this._activeTabSlide(tabIndex)
    },
    closeEdit() {
      if (this.$store.state.isNewPresentation) return
      this.$store.state.isOpenEditSlidePowerPoint = false
    },
    _trackSwitchTab(tabName) {
      const eventAction = 'Create new slide modal > Switch tab'
      const trackData = {
        eventAction,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventLabel: tabName,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },
    activeTabType() {
      this._activeTabSlide(1)
      this._trackSwitchTab('Type')
    },
    activeAudioTab(tabName) {
      this.setRightPanelContainerActiveTabAction(tabName)
      const eventLabel = tabName === this.rightPanelContainerActiveTabs.TAB_AUDIO_SLIDE ? 'Slide' : 'Presentation'
      utils.trackGA4AndMixpanel('Audio Tab > Switch tab', {
        eventAction: 'Audio Tab > Switch tab',
        eventLabel,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        userId: this.currentUser.id,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';

.tab-type-presentation {
  ul {
    margin: 0;
    padding: 0;
    overflow: hidden;
    .tab-type-presentation-item {
      @apply text-base text-secondary-100;
      padding-bottom: 12px;
      list-style: none;
      float: left;
      cursor: pointer;
      position: relative;
      font-weight: bold;
      &:not(:last-child) {
        margin-right: 2rem;
      }
      &.is-static-tab {
        padding: 0;
        cursor: default;
      }
      &.active {
        border-bottom: 3px solid $primary-color;
        @apply text-primary-90;
      }
      &:hover:not(.is-static-tab) {
        color: $primary-color;
      }
      &.disabled {
        @apply text-secondary-100 cursor-not-allowed;
      }
      .fa-volume-up, .music-note-icon {
        display: none;
      }

      .fa-exclamation-circle {
        color: #ffb12e;
        position: absolute;
        font-size: 0.9em;
      }
    }
    @media (max-width: 360px) {
      li {
        font-size: 16px;
      }
    }
  }
}

@media (max-width: 798px) {
  .tab-type-presentation ul .tab-type-presentation-item {
    height: 44px;
    .fa-volume-up {
      display: block;
      position: relative;
      top: 4px;
    }
    .music-note-icon {
      display: block;
      width: 20;
      height: 20;
    }
    .text-tab {
      display: none;
    }
  }
}
.btn-close__icon {
  position: absolute;
  right: 8px;
  top: 19px;
  cursor: pointer;
  display: none;
}

@media (max-width: 1180px) {
  .btn-close__icon {
    display: block;
  }
}

@media (max-width: 768px) {
  .btn-close__icon {
    display: none;
  }
}
</style>
